import { useEffect, useState } from 'react';

export type ForumCategory = {
  slug: string;
  name: string;
  color: string;
  topicsCount: number;
};

export type ForumTopic = {
  id: string;
  title: string;
  body?: string;
  category: string;
  tags: string[];
  participants: { initial: string; color: string }[];
  replies: number;
  views: number;
  createdAt: number;
};

export const FORUM_CATEGORIES: ForumCategory[] = [
  { slug: 'buying', name: 'Buying Property', color: 'bg-emerald-500', topicsCount: 14 },
  { slug: 'renting', name: 'Renting Property', color: 'bg-sky-500', topicsCount: 9 },
  { slug: 'selling', name: 'Selling Property', color: 'bg-amber-500', topicsCount: 6 },
  { slug: 'investment', name: 'Investment', color: 'bg-fuchsia-500', topicsCount: 11 },
  { slug: 'legal', name: 'Legal & Taxation', color: 'bg-rose-500', topicsCount: 5 },
  { slug: 'neighborhoods', name: 'Neighborhoods', color: 'bg-indigo-500', topicsCount: 8 },
];

export const FORUM_TAGS = [
  'ethio-property', 'investment', 'buy-property', 'property-buy',
  'selling-property', 'addis-ababa', 'mortgage', 'rent', 'renting-property',
];

const palette = ['bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-rose-500', 'bg-indigo-500', 'bg-fuchsia-500'];
export const avatarFor = (s: string) => ({
  initial: s[0]?.toUpperCase() || 'U',
  color: palette[Math.abs((s.charCodeAt(0) || 0)) % palette.length],
});

const day = 86400000;
const seed = (now: number): ForumTopic[] => [
  { id: '1', title: "What's one feature you thought didn't matter—but now you can't live without?", category: 'buying', tags: ['ethio-property', 'investment', 'buy-property'], participants: [avatarFor('E')], replies: 0, views: 2, createdAt: now - 1 * day },
  { id: '2', title: 'Which city in Ethiopia has the best real estate potential in the next 5 years?', category: 'investment', tags: ['ethio-property', 'investment', 'addis-ababa'], participants: [avatarFor('A')], replies: 0, views: 1, createdAt: now - 3 * day },
  { id: '3', title: "Is 'near future development' a real opportunity or just a sales tactic?", category: 'investment', tags: ['investment', 'buy-property'], participants: [avatarFor('E'), avatarFor('D'), avatarFor('J'), avatarFor('M')], replies: 3, views: 5, createdAt: now - 5 * day },
  { id: '4', title: 'Have you ever changed your mind about a property after visiting it? What changed?', category: 'buying', tags: ['buy-property', 'investment'], participants: [avatarFor('E')], replies: 0, views: 2, createdAt: now - 6 * day },
  { id: '5', title: "What's the biggest red flag you've noticed in rental listings?", category: 'renting', tags: ['rent', 'renting-property'], participants: [avatarFor('E'), avatarFor('N'), avatarFor('J')], replies: 2, views: 4, createdAt: now - 8 * day },
  { id: '6', title: "What's more important: society development speed or location?", category: 'investment', tags: ['investment', 'buy-property'], participants: [avatarFor('E')], replies: 0, views: 1, createdAt: now - 9 * day },
  { id: '7', title: "Would you pay more for a 'ready-to-move' home or wait for a cheaper under-construction one?", category: 'buying', tags: ['buy-property'], participants: [avatarFor('E')], replies: 0, views: 1, createdAt: now - 10 * day },
  { id: '8', title: 'Has anyone regretted choosing a corner or main-road facing property?', category: 'buying', tags: ['buy-property'], participants: [avatarFor('E'), avatarFor('N')], replies: 1, views: 3, createdAt: now - 11 * day },
];

export type ForumReply = {
  id: string;
  topicId: string;
  body: string;
  author: string;
  createdAt: number;
};

const KEY = 'ere_forum_topics_v1';
const REPLIES_KEY = 'ere_forum_replies_v1';

function load(): ForumTopic[] {
  if (typeof window === 'undefined') return seed(Date.now());
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return seed(Date.now());
    const parsed = JSON.parse(raw) as ForumTopic[];
    return Array.isArray(parsed) && parsed.length ? parsed : seed(Date.now());
  } catch {
    return seed(Date.now());
  }
}

function loadReplies(): ForumReply[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(REPLIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ForumReply[];
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

const OWNED_KEY = 'ere_forum_owned_v1';

const listeners = new Set<() => void>();
let topics: ForumTopic[] = load();
let replies: ForumReply[] = loadReplies();

// Defensive: on initial load, recompute reply counts from the persisted replies
// so the table stays accurate even if older builds left them out of sync.
(function recomputeReplyCounts() {
  const counts = new Map<string, number>();
  for (const r of replies) counts.set(r.topicId, (counts.get(r.topicId) || 0) + 1);
  let dirty = false;
  for (const t of topics) {
    const c = counts.get(t.id) || 0;
    if (t.replies !== c) { t.replies = c; dirty = true; }
  }
  if (dirty && typeof window !== 'undefined') {
    try { localStorage.setItem(KEY, JSON.stringify(topics)); } catch { /* ignore */ }
  }
})();

function loadOwned(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(OWNED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch { return new Set(); }
}
let owned: Set<string> = loadOwned();

function markOwned(id: string) {
  owned.add(id);
  try { localStorage.setItem(OWNED_KEY, JSON.stringify([...owned])); } catch { /* ignore */ }
}

export function isOwnedTopic(id: string) { return owned.has(id); }
export function isOwnedReply(id: string) { return owned.has(id); }

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(topics));
    localStorage.setItem(REPLIES_KEY, JSON.stringify(replies));
  } catch { /* ignore */ }
  listeners.forEach((fn) => fn());
}

export function updateTopic(id: string, patch: { body?: string; category?: string; tags?: string[] }) {
  const t = topics.find((x) => x.id === id);
  if (!t) return null;
  if (patch.body !== undefined) t.body = patch.body.trim();
  if (patch.category !== undefined) t.category = patch.category;
  if (patch.tags !== undefined) t.tags = patch.tags.map((s) => s.trim()).filter(Boolean).slice(0, 5);
  persist();
  return t;
}

export function deleteReply(replyId: string) {
  const r = replies.find((x) => x.id === replyId);
  if (!r) return false;
  const topic = topics.find((t) => t.id === r.topicId);
  replies = replies.filter((x) => x.id !== replyId);
  if (topic) topic.replies = Math.max(0, topic.replies - 1);
  owned.delete(replyId);
  try { localStorage.setItem(OWNED_KEY, JSON.stringify([...owned])); } catch { /* ignore */ }
  persist();
  return true;
}

export function updateReply(replyId: string, body: string) {
  const r = replies.find((x) => x.id === replyId);
  if (!r) return null;
  r.body = body.trim();
  persist();
  return r;
}

export function getTopic(id: string): ForumTopic | undefined {
  return topics.find((t) => t.id === id);
}

export function incrementViews(id: string) {
  const t = topics.find((x) => x.id === id);
  if (!t) return;
  t.views += 1;
  persist();
}

export function addReply(input: { topicId: string; body: string; author?: string }): ForumReply | null {
  const topic = topics.find((t) => t.id === input.topicId);
  if (!topic) return null;
  const reply: ForumReply = {
    id: crypto.randomUUID(),
    topicId: input.topicId,
    body: input.body.trim(),
    author: input.author || 'You',
    createdAt: Date.now(),
  };
  replies = [...replies, reply];
  topic.replies += 1;
  markOwned(reply.id);
  const av = avatarFor(reply.author);
  if (!topic.participants.some((p) => p.initial === av.initial)) {
    topic.participants = [...topic.participants, av];
  }
  persist();
  return reply;
}

export function useTopicReplies(topicId: string): ForumReply[] {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return replies.filter((r) => r.topicId === topicId).sort((a, b) => a.createdAt - b.createdAt);
}

export function addTopic(input: { title: string; body?: string; category: string; tags: string[]; author?: string }) {
  const topic: ForumTopic = {
    id: crypto.randomUUID(),
    title: input.title.trim(),
    body: input.body?.trim() || '',
    category: input.category,
    tags: input.tags.map((t) => t.trim()).filter(Boolean),
    participants: [avatarFor(input.author || 'You')],
    replies: 0,
    views: 0,
    createdAt: Date.now(),
  };
  topics = [topic, ...topics];
  markOwned(topic.id);
  persist();
  return topic;
}

export function useForumTopics(): ForumTopic[] {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return topics;
}

export function relativeTime(ms: number) {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return `${Math.floor(d / 30)}mo`;
}
